/**
 * The app reports its own problems to the server (seen at /api/diag/crash), so a crash on a phone can be diagnosed
 * from measurements instead of guesses (19.10.18):
 * - page errors and failed promises;
 * - the page froze (nothing ran for over 3 s while it was on screen);
 * - the page died while on screen (the phone killed it, usually for memory) and came back reloaded: noticed on the
 *   next start, with its size and memory from just before.
 */
const KEY = "joy-alive";
let sent = 0;

function size() {
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  return {
    nodes: document.getElementsByTagName("*").length,
    ...(mem ? { heapMb: Math.round(mem.usedJSHeapSize / 1048576), heapLimitMb: Math.round(mem.jsHeapSizeLimit / 1048576) } : {}),
    page: location.pathname + location.search.replace(/[?&](token|code)=[^&]*/g, ""),
  };
}

function report(kind: string, message: string, detail: Record<string, unknown> = {}) {
  if (sent >= 15) return; // one bad page can't flood the log
  sent++;
  try {
    void fetch("/api/diag/app-error", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind, message: message.slice(0, 1500), detail: { ...detail, ...size() } }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let reporting break the app
  }
}

export function startCrashWatch() {
  // Each open copy of the app (a tab, the installed app) keeps its own heartbeat while it's on screen. One that
  // stopped beating without leaving cleanly died on screen. Checked 25 s after start, so copies still open elsewhere
  // (which beat every 10 s) are never mistaken for it.
  const id = Math.random().toString(36).slice(2, 10);
  const mine = `${KEY}:${id}`;
  setTimeout(() => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k?.startsWith(`${KEY}:`) || k === mine) continue;
        const prev = JSON.parse(localStorage.getItem(k) ?? "null") as { at: number; page: string; up: number } | null;
        const age = prev ? Date.now() - prev.at : Infinity;
        if (age < 20_000) continue; // still alive somewhere
        localStorage.removeItem(k);
        if (prev && age < 15 * 60_000) report("page killed", `The app closed by itself while open (on ${prev.page}, after ${Math.round(prev.up / 60_000)} min) and restarted.`, { before: prev });
      }
    } catch {
      // storage not available
    }
  }, 25_000);
  const started = Date.now();
  const beat = () => {
    try {
      if (document.visibilityState === "visible") localStorage.setItem(mine, JSON.stringify({ at: Date.now(), up: Date.now() - started, ...size() }));
    } catch {
      // not kept
    }
  };
  // Leaving the app normally (switching apps, closing it) is not a crash.
  const clean = () => {
    try {
      localStorage.removeItem(mine);
    } catch {
      // not kept
    }
  };
  document.addEventListener("visibilitychange", () => (document.visibilityState === "hidden" ? clean() : beat()));
  addEventListener("pagehide", clean);
  beat();
  setInterval(beat, 10_000);

  addEventListener("error", (e) => {
    if (!e.message) return; // a picture that didn't load etc.
    report("page error", e.message, { at: e.filename ? `${e.filename.replace(location.origin, "")}:${e.lineno}:${e.colno}` : undefined, stack: (e.error as Error | undefined)?.stack?.slice(0, 1500) });
  });
  addEventListener("unhandledrejection", (e) => {
    const r = e.reason as Error | undefined;
    const msg = r?.message ?? String(e.reason);
    // Network drops and our own timeouts are normal on phones (they already show a message).
    // So is the calling library dropping old requests when it reconnects ("Stale request cancelled").
    if (/abort|network|failed to fetch|load failed|timed out|NetworkError|Stale request cancelled/i.test(msg)) return;
    report("failed promise", msg, { stack: r?.stack?.slice(0, 1500) });
  });

  // Freezes: a 1-second tick that ran late by over 3 s while the app was on screen.
  let expect = Date.now() + 1000;
  setInterval(() => {
    const late = Date.now() - expect;
    expect = Date.now() + 1000;
    if (late > 3000 && document.visibilityState === "visible" && !document.hidden) report("app froze", `The app froze for ${(late / 1000).toFixed(1)} s.`);
  }, 1000);
}
